import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ExternalLink, Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../../../services/supabase';
import { SPACING } from '../../../theme';
import { isUuid } from '../../../utils/postgrest';

interface DropInSession {
  id: string;
  user_id: string | null;
  drop_slug: string;
  lang: string;
  position_sec: number;
  session_length_sec: number;
  turns: number;
  close_reason: string | null;
  tools_used: string[];
  transcript: TranscriptEntry[] | null;
  ended_at: string;
}

interface TranscriptEntry {
  role: string;
  content: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const SessionDetail = ({ session, storyIdMap }: { session: DropInSession; storyIdMap: Record<string, string> }) => {
  const transcript = Array.isArray(session.transcript)
    ? session.transcript
    : [];
  const storyId = storyIdMap[session.drop_slug];

  return (
    <Box sx={{ p: SPACING.cardPadding, overflow: 'auto', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">
          {session.drop_slug}
        </Typography>
        {storyId && (
          <Tooltip title="Open story" arrow>
            <IconButton
              size="small"
              href={`/stories/${storyId}/edit`}
              component="a"
            >
              <ExternalLink size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.componentGap,
          mb: SPACING.sectionVertical,
        }}
      >
        <DetailField label="Date" value={format(new Date(session.ended_at), 'PPpp')} />
        <DetailField label="Language" value={session.lang} />
        <DetailField label="Position" value={formatDuration(session.position_sec)} />
        <DetailField label="Session Length" value={formatDuration(session.session_length_sec)} />
        <DetailField label="Turns" value={String(session.turns)} />
        <DetailField label="Close Reason" value={session.close_reason || '—'} />
        {session.user_id && <DetailField label="User ID" value={session.user_id} />}
      </Box>

      {session.tools_used.length > 0 && (
        <Box sx={{ mb: SPACING.sectionVertical }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Tools Used
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {session.tools_used.map((tool) => (
              <Chip key={tool} label={tool} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {transcript.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Transcript ({transcript.length} messages)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {transcript.map((entry, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: entry.role === 'user' ? 'action.selected' : 'action.hover',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  {entry.role}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {entry.content}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {transcript.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No transcript available
        </Typography>
      )}
    </Box>
  );
};

const DetailField = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

export const DropInSessions = () => {
  const [sessions, setSessions] = useState<DropInSession[]>([]);
  const [selected, setSelected] = useState<DropInSession | null>(null);
  const [storyIdMap, setStoryIdMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]!);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [turnsOnly, setTurnsOnly] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);

    const from = page * rowsPerPage;
    const to = from + rowsPerPage - 1;

    let query = supabase
      .from('drop_in_sessions')
      .select('*', { count: 'exact' })
      .order('ended_at', { ascending: false })
      .range(from, to);

    if (searchTerm) {
      // user_id is a uuid column: filtering it with non-UUID text makes
      // PostgREST reject the whole query (22P02), so branch on input shape.
      query = isUuid(searchTerm)
        ? query.eq('user_id', searchTerm)
        : query.ilike('drop_slug', `%${searchTerm}%`);
    }
    if (turnsOnly) {
      query = query.gt('turns', 0);
    }

    const { data, error, count } = await query;

    if (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch drop_in_sessions', error);
      setFetchError(error.message);
      setLoading(false);
      return;
    }
    setFetchError(null);

    const rows = (data ?? []) as DropInSession[];
    setSessions(rows);
    setTotal(count ?? 0);
    setSelected(rows[0] ?? null);

    // Resolve story slugs → IDs for linking
    const slugs = [...new Set(rows.map((r) => r.drop_slug))];
    if (slugs.length > 0) {
      const { data: stories } = await supabase
        .from('stories')
        .select('id, slug')
        .in('slug', slugs);
      if (stories) {
        const map: Record<string, string> = {};
        for (const s of stories) map[s.slug] = s.id;
        setStoryIdMap(map);
      }
    }

    setLoading(false);
  }, [page, rowsPerPage, searchTerm, turnsOnly]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPage(0);
    setSearchTerm('');
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Toolbar: search + refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: SPACING.componentGap }}>
        <TextField
          placeholder="Search by user ID or story slug"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end" sx={{ mr: -1 }}>
                  <X size={18} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={turnsOnly}
              onChange={(e) => { setTurnsOnly(e.target.checked); setPage(0); }}
            />
          }
          label="Turns > 0"
          sx={{ ml: 1, whiteSpace: 'nowrap' }}
        />
        <Button variant="outlined" size="small" onClick={fetchSessions} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load sessions: {fetchError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sessions.length === 0 ? (
        <Box sx={{ p: SPACING.cardPadding }}>
          <Typography color="text.secondary">
            {searchTerm ? 'No sessions match your search.' : 'No drop-in sessions yet.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 220px)' }}>
          {/* Master list */}
          <Paper
            variant="outlined"
            sx={{
              width: 320,
              minWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1,
            }}
          >
            <List disablePadding sx={{ overflow: 'auto', flex: 1 }}>
              {sessions.map((session, i) => (
                <Box key={session.id}>
                  {i > 0 && <Divider />}
                  <ListItemButton
                    selected={selected?.id === session.id}
                    onClick={() => setSelected(session)}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {session.drop_slug}
                          </Typography>
                          <Chip
                            label={`${session.turns} turns`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, flexShrink: 0 }}
                          />
                        </Box>
                      }
                      secondary={format(new Date(session.ended_at), 'MMM d, HH:mm')}
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              labelRowsPerPage=""
              sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
            />
          </Paper>

          {/* Detail panel */}
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              ml: SPACING.componentGap,
              overflow: 'auto',
              borderRadius: 1,
            }}
          >
            {selected ? (
              <SessionDetail session={selected} storyIdMap={storyIdMap} />
            ) : (
              <Box sx={{ p: SPACING.cardPadding }}>
                <Typography color="text.secondary">Select a session</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};
